import React, { useState } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { UserPlus } from 'lucide-react';

const Signup = () => {
  const [form, setForm] = useState({ name: "", email: "", password: "", code: "" });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (form.code !== "Z171717") {
      setSnackbar({ open: true, message: "Invalid code. Please enter the correct code.", severity: "error" });
      setLoading(false);
      navigate("/success");
      return;
    }

    try {
      await axios.post("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/register", form);
      setSnackbar({ open: true, message: "Signup successful! Redirecting...", severity: "success" });
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || "Signup failed", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        px: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 380 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: '#2383e2',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <UserPlus size={22} color="white" />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            Create an account
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Get started with inventory tracking
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>Name</Typography>
              <TextField fullWidth name="name" placeholder="Your name" required onChange={handleChange} size="small" />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>Email</Typography>
              <TextField fullWidth name="email" type="email" placeholder="you@example.com" required onChange={handleChange} size="small" />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>Password</Typography>
              <TextField fullWidth name="password" type="password" placeholder="Create a password" required onChange={handleChange} size="small" />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 0.5 }}>Invite Code</Typography>
              <TextField fullWidth name="code" placeholder="Enter code" required onChange={handleChange} size="small" />
            </Box>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{ mt: 1, py: 1.25, bgcolor: '#2383e2', fontWeight: 600, '&:hover': { bgcolor: '#1b6ec2' } }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : "Create account"}
            </Button>
          </Stack>
        </form>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Already have an account?{' '}
            <Typography
              component="span"
              variant="body2"
              onClick={() => navigate("/login")}
              sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
            >
              Sign in
            </Typography>
          </Typography>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Signup;
